export const SAVE_ERROR_TOAST_DURATION = 8000;

/**
 * Turns a Laravel validation bag ({ field: [messages] }) into { field: firstMessage }.
 *
 * @param {Record<string, string[]|string>|null|undefined} errors
 * @returns {Record<string, string>}
 */
export function toFieldErrors(errors) {
    return Object.fromEntries(
        Object.entries(errors || {})
            .map(([field, messages]) => [field, Array.isArray(messages) ? messages[0] : messages])
            .filter(([, message]) => Boolean(message))
    );
}

/**
 * Joins field messages into one readable sentence list, falling back when there are none.
 *
 * @param {Record<string, string>} errors
 * @param {string} [fallback]
 * @returns {string}
 */
export function describeErrors(errors, fallback = '') {
    const sentences = Object.values(errors || {})
        .map(message =>
            String(message)
                .trim()
                .replace(/[.!?]$/, '')
        )
        .filter(Boolean)
        .map(message => `${message}.`);

    return sentences.length ? sentences.join(' ') : fallback;
}

/**
 * Validates values against a yup schema and returns { field: message } for every failing field.
 * Anything other than a yup ValidationError is logged and treated as "no client errors" so a client-side
 * bug never makes a location unsavable; the server still validates the request.
 *
 * @param {import('yup').Schema} schema
 * @param {object} values
 * @returns {Promise<Record<string, string>>}
 */
export async function clientValidationErrors(schema, values) {
    try {
        await schema.validate(values, { abortEarly: false });

        return {};
    } catch (error) {
        if (error?.name !== 'ValidationError') {
            console.error('Location form validation failed unexpectedly', error);

            return {};
        }

        const issues = error.inner?.length ? error.inner : [error];

        return issues.reduce((errors, issue) => {
            if (issue.path && !(issue.path in errors)) {
                errors[issue.path] = issue.message;
            }

            return errors;
        }, {});
    }
}
